<script setup>
import {ref, computed, onMounted, watch, nextTick} from 'vue'

// 无线电字母解释法对照表
const phoneticAlphabet = {
  'A': 'Alpha',
  'B': 'Bravo',
  'C': 'Charlie',
  'D': 'Delta',
  'E': 'Echo',
  'F': 'Foxtrot',
  'G': 'Golf',
  'H': 'Hotel',
  'I': 'India',
  'J': 'Juliet',
  'K': 'Kilo',
  'L': 'Lima',
  'M': 'Mike',
  'N': 'November',
  'O': 'Oscar',
  'P': 'Papa',
  'Q': 'Quebec',
  'R': 'Romeo',
  'S': 'Sierra',
  'T': 'Tango',
  'U': 'Uniform',
  'V': 'Victor',
  'W': 'Whiskey',
  'X': 'X-ray',
  'Y': 'Yankee',
  'Z': 'Zulu',
  '1': 'One',
  '2': 'Two',
  '3': 'Three',
  '4': 'Four',
  '5': 'Five',
  '6': 'Six',
  '7': 'Seven',
  '8': 'Eight',
  '9': 'Nine',
  '0': 'Zero'
}

// 当前呼号
const currentCallsign = ref('')
const phoneticCallsign = computed(() => {
  if (!currentCallsign.value) return []

  return Array.from(currentCallsign.value).map(char => {
    return {
      char: char,
      phonetic: phoneticAlphabet[char.toUpperCase()] || char
    }
  })
})

// 语音设置
const voiceSettings = ref({
  rate: 1.0,     // 语速 (0.1 到 2)
  voice: null    // 选定的声音
})

// 自动生成相关状态
const isAutoGenerating = ref(false)
const autoGenerateInterval = ref(6) // 默认间隔6秒
const autoGenerateTimer = ref(null)
const countdownValue = ref(0)
const recentCallsigns = ref([]) // 存储最近3个呼号
const allCallsigns = ref([]) // 存储所有生成过的呼号

// 添加一个专门用于显示的数组
const displayCallsigns = ref([])

// 更新显示数组的函数
const updateDisplayCallsigns = () => {
  displayCallsigns.value = recentCallsigns.value.slice(0, 3)
}

// 开始自动生成
const startAutoGenerate = () => {
  if (isAutoGenerating.value) return

  isAutoGenerating.value = true
  countdownValue.value = autoGenerateInterval.value

  // 立即生成一个新呼号
  generateAndSpeak()

  // 设置定时器
  autoGenerateTimer.value = setInterval(() => {
    countdownValue.value -= 1

    if (countdownValue.value <= 0) {
      generateAndSpeak()
      countdownValue.value = autoGenerateInterval.value
    }
  }, 1000)
}

// 停止自动生成
const stopAutoGenerate = () => {
  if (!isAutoGenerating.value) return

  isAutoGenerating.value = false
  if (autoGenerateTimer.value) {
    clearInterval(autoGenerateTimer.value)
    autoGenerateTimer.value = null
  }
}

// 生成呼号并朗读
const generateAndSpeak = () => {
  generateCallsign()

  // 短暂延迟后朗读，确保UI更新
  setTimeout(() => {
    speakPhonetic()
  }, 100)
}

// 生成呼号并添加到历史记录
const generateAndAddCallsign = () => {
  generateCallsign()
  // 不需要再次添加到历史记录，因为已经在generateCallsign中添加了
}

// 添加到最近呼号列表
const addToRecentCallsigns = (callsign) => {
  const timestamp = new Date().toLocaleTimeString()

  // 添加到最近列表开头
  recentCallsigns.value.unshift({
    callsign: callsign,
    timestamp: timestamp
  })

  // 保持最多10个记录
  if (recentCallsigns.value.length > 10) {
    recentCallsigns.value.pop()
  }

  // 添加到所有呼号历史记录
  allCallsigns.value.unshift({
    callsign: callsign,
    timestamp: timestamp
  })

  // 更新显示数组
  updateDisplayCallsigns()

  // 保存到本地存储
  saveCallsignHistory()
}

// 保存呼号历史到本地存储
const saveCallsignHistory = () => {
  localStorage.setItem('callsignHistory', JSON.stringify(allCallsigns.value))
}

// 从本地存储加载呼号历史
const loadCallsignHistory = () => {
  const savedHistory = localStorage.getItem('callsignHistory')
  if (savedHistory) {
    try {
      allCallsigns.value = JSON.parse(savedHistory)

      // 更新最近呼号列表
      recentCallsigns.value = allCallsigns.value.slice(0, 10)

      // 更新显示数组
      updateDisplayCallsigns()
    } catch (e) {
      console.error('加载呼号历史失败:', e)
    }
  }
}

// 清空历史记录
const clearCallsignHistory = () => {
  showModal('确定要清空所有呼号历史记录吗？', '确认操作', () => {
    // 执行清空操作
    allCallsigns.value = []
    recentCallsigns.value = []
    localStorage.removeItem('callsignHistory')
  })
}

// 导出历史记录为CSV
const exportHistory = () => {
  if (allCallsigns.value.length === 0) {
    showModal('没有可导出的历史记录')
    return
  }

  // 创建CSV内容
  const csvContent = 'data:text/csv;charset=utf-8,'
      + '呼号,时间\n'
      + allCallsigns.value.map(item => `${item.callsign},${item.timestamp}`).join('\n')

  // 创建下载链接
  const encodedUri = encodeURI(csvContent)
  const link = document.createElement('a')
  link.setAttribute('href', encodedUri)
  link.setAttribute('download', `呼号训练记录_${new Date().toLocaleDateString()}.csv`)
  document.body.appendChild(link)

  // 触发下载
  link.click()
  document.body.removeChild(link)
}

// 在组件销毁时清除定时器
const onBeforeUnmount = () => {
  if (autoGenerateTimer.value) {
    clearInterval(autoGenerateTimer.value)
  }
}

// 可用的语音列表
const availableVoices = ref([])

// 语音别名映射 - 扩展更多常见语音
const voiceAliases = {
  // Google 语音
  'Google US English': '美式英语 (女声)',
  'Google UK English Female': '英式英语 (女声)',
  'Google UK English Male': '英式英语 (男声)',
  'Google 普通话（中国大陆）': '中文普通话 (女声)',
  'Google 粤語（香港）': '粤语 (女声)',

  // Microsoft Edge 语音
  'Microsoft David - English (United States)': '美式英语 (男声)',
  'Microsoft Mark - English (United States)': '美式英语 (男声)',
  'Microsoft Zira - English (United States)': '美式英语 (女声)',
  'Microsoft Guy - English (United States)': '美式英语 (男声)',
  'Microsoft Catherine - English (United Kingdom)': '英式英语 (女声)',
  'Microsoft Susan - English (United Kingdom)': '英式英语 (女声)',
  'Microsoft Hazel - English (United Kingdom)': '英式英语 (女声)',
  'Microsoft George - English (United Kingdom)': '英式英语 (男声)',
  'Microsoft Huihui - Chinese (Simplified, PRC)': '中文普通话 (女声)',
  'Microsoft Yaoyao - Chinese (Simplified, PRC)': '中文普通话 (女声)',
  'Microsoft Kangkang - Chinese (Simplified, PRC)': '中文普通话 (男声)',
  'Microsoft Tracy - Chinese (Traditional, Hong Kong S.A.R.)': '粤语 (女声)',
  'Microsoft Danny - Chinese (Traditional, Hong Kong S.A.R.)': '粤语 (男声)',

  // Microsoft Neural 语音
  'Microsoft David Neural - English (United States)': '美式英语 (高质量男声)',
  'Microsoft Zira Neural - English (United States)': '美式英语 (高质量女声)',
  'Microsoft Guy Neural - English (United States)': '美式英语 (高质量男声)',
  'Microsoft Catherine Neural - English (United Kingdom)': '英式英语 (高质量女声)',
  'Microsoft Susan Neural - English (United Kingdom)': '英式英语 (高质量女声)',
  'Microsoft Hazel Neural - English (United Kingdom)': '英式英语 (高质量女声)',
  'Microsoft George Neural - English (United Kingdom)': '英式英语 (高质量男声)',
  'Microsoft Xiaoxiao Neural - Chinese (Mainland)': '中文普通话 (高质量女声)',
  'Microsoft Yunyang Neural - Chinese (Mainland)': '中文普通话 (高质量男声)',
  'Microsoft HiuGaai Neural - Chinese (Hong Kong)': '粤语 (高质量女声)',
  'Microsoft WanLung Neural - Chinese (Hong Kong)': '粤语 (高质量男声)',

  // Safari/iOS 语音
  'Samantha': '美式英语 (女声)',
  'Alex': '美式英语 (男声)',
  'Daniel': '英式英语 (男声)',
  'Karen': '英式英语 (女声)',
  'Moira': '爱尔兰英语 (女声)',
  'Tessa': '澳大利亚英语 (女声)',
  'Sin-ji': '粤语 (女声)',
  'Ting-Ting': '中文普通话 (女声)',

  // Firefox 语音
  'urn:moz-tts:sapi:Microsoft David Desktop - English (United States)?en-US': '美式英语 (男声)',
  'urn:moz-tts:sapi:Microsoft Zira Desktop - English (United States)?en-US': '美式英语 (女声)'
}

// 获取语音的显示名称 - 使用别名替换而非标记
const getVoiceDisplayName = (voice) => {
  // 检查是否有预定义的别名
  if (voiceAliases[voice.name]) {
    return voiceAliases[voice.name]
  }

  // 检查是否是增强版语音
  const isEnhanced = voice.name.includes('Enhanced') ||
      voice.name.includes('Premium') ||
      voice.name.includes('Neural') ||
      voice.localService === false

  // 提取语音的基本名称
  let baseName = voice.name
      .replace(' Enhanced', '')
      .replace(' Premium', '')
      .replace(' Neural', '')

  // 截断过长的名称
  if (baseName.length > 20) {
    baseName = baseName.substring(0, 18) + '...'
  }

  // 确定性别标识
  let genderLabel = ''
  const lowerName = voice.name.toLowerCase()
  if (lowerName.includes('female') ||
      lowerName.includes('zira') ||
      lowerName.includes('susan') ||
      lowerName.includes('hazel') ||
      lowerName.includes('catherine') ||
      lowerName.includes('tracy') ||
      lowerName.includes('huihui') ||
      lowerName.includes('yaoyao') ||
      lowerName.includes('xiaoxiao') ||
      lowerName.includes('hiugaai')) {
    genderLabel = '女声'
  } else if (lowerName.includes('male') ||
      lowerName.includes('david') ||
      lowerName.includes('mark') ||
      lowerName.includes('george') ||
      lowerName.includes('danny') ||
      lowerName.includes('kangkang') ||
      lowerName.includes('yunyang') ||
      lowerName.includes('wanlung')) {
    genderLabel = '男声'
  }

  // 确定语言标识
  let langLabel = ''
  if (voice.lang === 'en-US') {
    langLabel = '美式英语'
  } else if (voice.lang === 'en-GB') {
    langLabel = '英式英语'
  } else if (voice.lang.startsWith('zh-CN')) {
    langLabel = '中文普通话'
  } else if (voice.lang.startsWith('zh-HK') || voice.lang.startsWith('zh-TW')) {
    langLabel = '粤语'
  } else if (voice.lang.startsWith('en-AU')) {
    langLabel = '澳大利亚英语'
  } else if (voice.lang.startsWith('en-IE')) {
    langLabel = '爱尔兰英语'
  } else if (voice.lang.startsWith('en')) {
    langLabel = '英语'
  } else if (voice.lang.startsWith('zh')) {
    langLabel = '中文'
  } else {
    // 使用语言代码的前两个字符
    langLabel = voice.lang.substring(0, 2) + '语'
  }

  // 构建显示名称
  let displayName = ''
  if (langLabel && genderLabel) {
    displayName = `${langLabel} (${genderLabel})`
    if (isEnhanced) {
      displayName = `${langLabel} (高质量${genderLabel})`
    }
  } else {
    // 如果无法确定语言或性别，使用简化的名称
    displayName = `${baseName} (${voice.lang.substring(0, 2)})`
    if (isEnhanced) {
      displayName = `${baseName} (高质量)`
    }
  }

  return displayName
}

// 更新可用的语音列表
const updateVoices = () => {
  if (!('speechSynthesis' in window)) return

  // 获取所有可用语音
  const allVoices = speechSynthesis.getVoices()

  // 去重：使用 Map 按 voiceURI 去重
  const uniqueVoicesMap = new Map()
  allVoices.forEach(voice => {
    if (!uniqueVoicesMap.has(voice.voiceURI)) {
      uniqueVoicesMap.set(voice.voiceURI, voice)
    }
  })

  // 转回数组
  const uniqueVoices = Array.from(uniqueVoicesMap.values())

  // 过滤英语语音
  const englishVoices = uniqueVoices.filter(voice =>
      voice.lang === 'en-US' || voice.lang === 'en-GB'
  )

  // 优先选择的语音名称列表（按优先级排序）
  const preferredVoiceNames = [
    // 高质量语音关键词
    'Enhanced', 'Premium', 'Neural',

    // 男声优质语音
    'Google UK English Male', 'Alex', 'Daniel',
    'Microsoft David', 'Microsoft Mark',

    // 女声优质语音
    'Google US English', 'Google UK English Female',
    'Samantha', 'Karen',
    'Microsoft Zira', 'Microsoft Susan'
  ]

  // 筛选出优先选择的语音
  const preferredVoices = englishVoices.filter(voice =>
      preferredVoiceNames.some(name => voice.name.includes(name))
  )

  // 确保至少有一个男声和一个女声
  let selectedVoices = []

  if (preferredVoices.length > 0) {
    // 尝试找出男声和女声
    const maleVoices = preferredVoices.filter(voice =>
        voice.name.includes('Male') ||
        voice.name.includes('David') ||
        voice.name.includes('Mark') ||
        voice.name.includes('Alex') ||
        voice.name.includes('Daniel')
    )

    const femaleVoices = preferredVoices.filter(voice =>
        voice.name.includes('Female') ||
        voice.name.includes('Zira') ||
        voice.name.includes('Susan') ||
        voice.name.includes('Samantha') ||
        voice.name.includes('Karen')
    )

    // 添加至少一个男声
    if (maleVoices.length > 0) {
      selectedVoices = selectedVoices.concat(maleVoices.slice(0, 2))
    }

    // 添加至少一个女声
    if (femaleVoices.length > 0) {
      selectedVoices = selectedVoices.concat(femaleVoices.slice(0, 2))
    }

    // 如果没有找到足够的男声或女声，添加其他优先语音
    if (selectedVoices.length < 4) {
      const remainingVoices = preferredVoices.filter(voice =>
          !selectedVoices.includes(voice)
      )
      selectedVoices = selectedVoices.concat(
          remainingVoices.slice(0, 4 - selectedVoices.length)
      )
    }
  }

  // 如果没有找到足够的优先语音，使用所有英语语音
  if (selectedVoices.length < 2) {
    selectedVoices = englishVoices.slice(0, 4)
  }

  availableVoices.value = selectedVoices

  // 如果有可用语音，默认选择第一个
  if (availableVoices.value.length > 0 && !voiceSettings.value.voice) {
    voiceSettings.value.voice = availableVoices.value[0]
  }
}

// 生成随机呼号
const generateCallsign = () => {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const numbers = '123456789'

  // 生成类似 BD8FOD 的呼号 (2字母+1数字+3字母)
  let callsign = 'B'

  // 前两位字母
  callsign += letters.charAt(Math.floor(Math.random() * letters.length))

  // 第三位数字
  callsign += numbers.charAt(Math.floor(Math.random() * numbers.length))

  // 后三位字母
  for (let i = 0; i < 3; i++) {
    callsign += letters.charAt(Math.floor(Math.random() * letters.length))
  }

  currentCallsign.value = callsign

  // 添加到历史记录
  addToRecentCallsigns(callsign)
}

// 语音合成
const speak = (text) => {
  if (!('speechSynthesis' in window)) {
    showModal('您的浏览器不支持语音合成')
    return
  }

  // 取消之前的语音
  window.speechSynthesis.cancel()

  // Chrome浏览器修复：重新启动语音合成
  if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
    window.speechSynthesis.cancel()
    // 短暂延迟后再尝试
    setTimeout(() => {
      speakWithFallback(text)
    }, 50)
    return
  }

  speakWithFallback(text)
}

// 带有后备方案的语音合成
const speakWithFallback = (text) => {
  const utterance = new SpeechSynthesisUtterance(text)

  // 应用语音设置
  utterance.rate = voiceSettings.value.rate

  // 如果有选定的语音，则使用它
  if (voiceSettings.value.voice) {
    utterance.voice = voiceSettings.value.voice
  }

  // 设备特定优化
  if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
    // iOS 设备优化
    utterance.pitch = 1.1
  } else if (/Android/.test(navigator.userAgent)) {
    // Android 设备优化
    utterance.pitch = 1.0
    // 某些 Android 设备在较慢的语速下表现更好
    if (utterance.rate > 1.0) {
      utterance.rate = Math.min(utterance.rate, 1.5)
    }
  }

  // Chrome 浏览器修复：添加事件监听器检测问题
  let speakStarted = false
  let chromeWorkaroundTimeout = null

  utterance.onstart = () => {
    speakStarted = true
    if (chromeWorkaroundTimeout) {
      clearTimeout(chromeWorkaroundTimeout)
    }
  }

  // 尝试播放语音
  window.speechSynthesis.speak(utterance)

  // Chrome 浏览器修复：如果语音没有开始，尝试重新启动语音合成
  chromeWorkaroundTimeout = setTimeout(() => {
    if (!speakStarted) {
      // 如果语音没有开始，尝试重新启动语音合成
      window.speechSynthesis.cancel()
      window.speechSynthesis.resume()

      // 重新创建一个新的语音实例
      const newUtterance = new SpeechSynthesisUtterance(text)
      newUtterance.rate = utterance.rate
      newUtterance.voice = utterance.voice
      newUtterance.pitch = utterance.pitch

      // 再次尝试播放
      window.speechSynthesis.speak(newUtterance)
    }
  }, 250)
}

// 添加Chrome浏览器语音合成修复
const setupChromeSpeechSynthesisFix = () => {
  // 检测是否是Chrome浏览器
  const isChrome = /Chrome/.test(navigator.userAgent) && !/Edge/.test(navigator.userAgent)

  if (isChrome && 'speechSynthesis' in window) {
    // Chrome有一个bug，如果页面在后台，语音合成会暂停
    // 这个函数每10秒调用一次resume()来防止这个问题
    setInterval(() => {
      if (window.speechSynthesis.paused) {
        window.speechSynthesis.resume()
      }
    }, 10000)
  }
}

// 朗读整个呼号的解释法
const speakPhonetic = () => {
  if (phoneticCallsign.value.length === 0) return

  const text = phoneticCallsign.value.map(item => item.phonetic).join(', ')
  speak(text)

  // 添加视觉反馈
  isSpeaking.value = true
  setTimeout(() => {
    isSpeaking.value = false
  }, text.length * 120) // 根据文本长度估算语音持续时间
}

// 朗读单个字符的解释法
const speakChar = (phonetic) => {
  speak(phonetic)

  // 添加视觉反馈
  isSpeaking.value = true
  setTimeout(() => {
    isSpeaking.value = false
  }, phonetic.length * 120) // 根据文本长度估算语音持续时间
}

// 语音状态
const isSpeaking = ref(false)

// 颜色模式查询引用
const colorSchemeQuery = ref(null)

// 更新滑块在颜色模式变化时的函数
const updateSlidersOnColorChange = () => {
  console.log('颜色模式变化，更新滑块')
  // 短暂延迟确保DOM已更新
  setTimeout(() => {
    updateAllSliders()
  }, 100)
}

// 自定义呼号
const customCallsign = ref('')
const setCustomCallsign = () => {
  // 验证格式：2字母+1数字+3字母
  const regex = /^[A-Za-z]{2}[0-9]{1}[A-Za-z]{3}$/

  if (regex.test(customCallsign.value)) {
    currentCallsign.value = customCallsign.value.toUpperCase()
    customCallsign.value = ''
  } else {
    showModal('请输入正确格式的呼号：2字母+1数字+3字母')
  }
}

// 保存语音设置到本地存储
const saveVoiceSettings = () => {
  // 由于 voice 对象不能直接序列化，我们只保存 voice 的 ID
  const settingsToSave = {
    rate: voiceSettings.value.rate,
    voice: voiceSettings.value.voice ? voiceSettings.value.voice.voiceURI : null
  }
  localStorage.setItem('callsignVoiceSettings', JSON.stringify(settingsToSave))
}

// 从本地存储加载语音设置
const loadVoiceSettings = () => {
  const savedSettings = localStorage.getItem('callsignVoiceSettings')
  if (savedSettings) {
    const parsed = JSON.parse(savedSettings)

    // 恢复基本设置，确保转换为数字类型
    voiceSettings.value.rate = Number(parsed.rate)

    // 如果有保存的 voice ID，找到对应的 voice 对象
    if (parsed.voice && availableVoices.value.length > 0) {
      const savedVoice = availableVoices.value.find(v => v.voiceURI === parsed.voice)
      if (savedVoice) {
        voiceSettings.value.voice = savedVoice
      }
    }
  }
}

// 添加用于处理滑块填充效果的方法
const updateSliderFill = (event) => {
  const slider = event.target
  // 计算百分比位置
  const min = parseFloat(slider.min)
  const max = parseFloat(slider.max)
  const val = parseFloat(slider.value)
  const percentage = ((val - min) / (max - min)) * 100

  // 设置CSS变量，用于控制渐变
  slider.style.setProperty('--range-progress', `${percentage}%`)

  // 检测是否是深色模式
  const isDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches

  // 根据颜色模式设置不同的渐变颜色
  if (isDarkMode) {
    const activeColor = getComputedStyle(document.documentElement).getPropertyValue('--color-secondary').trim() || '#4db6ff'
    const inactiveColor = '#444'
    // 应用线性渐变
    slider.style.background = `linear-gradient(to right, ${activeColor} 0%, ${activeColor} ${percentage}%, ${inactiveColor} ${percentage}%, ${inactiveColor} 100%)`
  } else {
    const activeColor = '#2196F3'
    const inactiveColor = '#ddd'
    // 应用线性渐变
    slider.style.background = `linear-gradient(to right, ${activeColor} 0%, ${activeColor} ${percentage}%, ${inactiveColor} ${percentage}%, ${inactiveColor} 100%)`
  }
}

// 更新所有滑块的填充效果
const updateAllSliders = () => {
  nextTick(() => {
    const sliders = document.querySelectorAll('.slider')
    sliders.forEach(slider => {
      // 计算百分比位置
      const min = parseFloat(slider.min)
      const max = parseFloat(slider.max)
      const val = parseFloat(slider.value)
      const percentage = ((val - min) / (max - min)) * 100

      // 设置CSS变量，用于控制渐变
      slider.style.setProperty('--range-progress', `${percentage}%`)

      // 检测是否是深色模式
      const isDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches

      // 根据颜色模式设置不同的渐变颜色
      if (isDarkMode) {
        const activeColor = getComputedStyle(document.documentElement).getPropertyValue('--color-secondary').trim() || '#4db6ff'
        const inactiveColor = '#444'
        // 应用线性渐变
        slider.style.background = `linear-gradient(to right, ${activeColor} 0%, ${activeColor} ${percentage}%, ${inactiveColor} ${percentage}%, ${inactiveColor} 100%)`
      } else {
        const activeColor = '#2196F3'
        const inactiveColor = '#ddd'
        // 应用线性渐变
        slider.style.background = `linear-gradient(to right, ${activeColor} 0%, ${activeColor} ${percentage}%, ${inactiveColor} ${percentage}%, ${inactiveColor} 100%)`
      }
    })
  })
}

// 在组件挂载后初始化
onMounted(() => {
  // 生成一个随机呼号
  generateCallsign()

  // 加载历史记录
  loadCallsignHistory()

  // 初始化语音
  if ('speechSynthesis' in window) {
    // Chrome 需要等待 voiceschanged 事件
    if (speechSynthesis.onvoiceschanged !== undefined) {
      speechSynthesis.onvoiceschanged = updateVoices
    }

    // 立即尝试获取语音
    updateVoices()

    // 设置Chrome浏览器语音合成修复
    setupChromeSpeechSynthesisFix()

    // 加载保存的设置
    setTimeout(() => {
      loadVoiceSettings()
      // 初始化滑块填充效果
      updateAllSliders()

      // 为所有滑块添加事件监听器
      const sliders = document.querySelectorAll('input[type="range"]')
      sliders.forEach(slider => {
        slider.addEventListener('input', updateSliderFill)
      })
    }, 100)
  }

  // 监听系统颜色模式变化
  if (window.matchMedia) {
    colorSchemeQuery.value = window.matchMedia('(prefers-color-scheme: dark)')
    // 添加事件监听器
    colorSchemeQuery.value.addEventListener('change', updateSlidersOnColorChange)

    // 立即检查当前颜色模式并更新滑块
    if (colorSchemeQuery.value.matches) {
      // 当前是深色模式
      setTimeout(() => {
        updateAllSliders()
      }, 200)
    }
  }
})

// 监听语音设置变化，更新滑块填充效果
watch(voiceSettings, () => {
  saveVoiceSettings()
  // 更新滑块填充效果
  updateAllSliders()
}, {deep: true})

// 组件卸载前清理
onBeforeUnmount(() => {
  // 停止自动生成
  stopAutoGenerate()

  // 清除滑块事件监听器
  const sliders = document.querySelectorAll('input[type="range"]')
  sliders.forEach(slider => {
    slider.removeEventListener('input', updateSliderFill)
  })

  // 清除颜色模式变化监听器
  if (colorSchemeQuery.value) {
    colorSchemeQuery.value.removeEventListener('change', updateSlidersOnColorChange)
  }
})

// 添加模态框相关状态
const modalVisible = ref(false)
const modalTitle = ref('提示')
const modalMessage = ref('')
const modalCallback = ref(null)

// 显示模态框
const showModal = (message, title = '提示', callback = null) => {
  modalTitle.value = title
  modalMessage.value = message
  modalCallback.value = callback
  modalVisible.value = true
}

// 关闭模态框
const closeModal = () => {
  if (modalCallback.value) {
    modalCallback.value()
    modalCallback.value = null
  }
  modalVisible.value = false
}
</script>

<template>
  <div class="callsign-container">
    <div class="callsign-display">
      <h2>当前呼号</h2>
      <div class="callsign">
        <span
            v-for="(item, index) in phoneticCallsign"
            :key="index"
            class="callsign-char"
            @click="speakChar(item.phonetic)"
            :class="{'digit': /\d/.test(item.char)}"
        >
          {{ item.char }}
        </span>
      </div>

      <div class="phonetic">
        <div
            v-for="(item, index) in phoneticCallsign"
            :key="index"
            class="phonetic-item"
            @click="speakChar(item.phonetic)"
        >
            <span class="char">{{ item.char }}</span>
            <span class="word">{{ item.phonetic }}</span>
        </div>
      </div>
    </div>

    <div class="actions">
      <div class="main-actions">
        <button @click="generateAndAddCallsign" class="generate-btn">生成新呼号</button>
        <button @click="speakPhonetic" class="speak-btn" :class="{ 'speaking': isSpeaking }">
          {{ isSpeaking ? '正在朗读...' : '朗读解释法' }}
        </button>
      </div>

      <!-- 自动生成控制 -->
      <div class="main-actions">
        <button
            @click="isAutoGenerating ? stopAutoGenerate() : startAutoGenerate()"
            :class="{'active': isAutoGenerating}"
            class="auto-btn"
        >
          {{ isAutoGenerating ? '停止生成' : '自动生成' }}
          <span v-if="isAutoGenerating" class="countdown">({{ countdownValue }}s)</span>
        </button>

        <div class="interval-control" v-if="!isAutoGenerating">
          <label>间隔(秒):</label>
          <input
              type="number"
              v-model.number="autoGenerateInterval"
              min="2"
              max="20"
              class="interval-input"
          />
        </div>
      </div>
    </div>

    <!-- 语音设置 -->
    <div class="voice-settings">
      <h3>语音设置</h3>
      <div class="setting-group">
        <label>语速</label>
        <input
            type="range"
            v-model.number="voiceSettings.rate"
            min="0"
            max="2"
            step="0.1"
            @input="updateSliderFill"
            class="slider"
        />
        <span class="setting-value">{{ voiceSettings.rate.toFixed(1) }}</span>
      </div>

      <div class="setting-group" v-if="availableVoices.length > 0">
        <label>语音</label>
        <select v-model="voiceSettings.voice">
          <option
              v-for="voice in availableVoices"
              :key="voice.voiceURI"
              :value="voice"
          >
            {{ getVoiceDisplayName(voice) }}
          </option>
        </select>
      </div>
    </div>

    <div class="custom-callsign">
      <h3>输入自定义呼号</h3>
      <div class="input-group">
        <input
            v-model="customCallsign"
            placeholder="例如: BD8FOD"
            maxlength="6"
            @keyup.enter="setCustomCallsign"
        />
        <button @click="setCustomCallsign">确定</button>
      </div>
      <p class="hint">格式: 2字母+1数字+3字母</p>
    </div>

    <!-- 最近呼号记录 -->
    <div class="callsigns-history">
      <div class="history-header">
        <h3>最近呼号记录</h3>
        <div class="history-actions">
          <button @click="exportHistory" class="toggle-btn">
            导出完整记录
          </button>
          <button @click="clearCallsignHistory" class="clear-btn" title="清空所有历史记录">
            清空
          </button>
        </div>
      </div>

      <div class="callsigns-list">
        <div
            v-for="(item, index) in recentCallsigns.slice(0, 3)"
            :key="index"
            class="callsign-item"
            @click="currentCallsign = item.callsign"
        >
          <span class="callsign-text">{{ item.callsign }}</span>
          <span class="callsign-time">{{ item.timestamp }}</span>
        </div>
        <div v-if="recentCallsigns.slice(0, 3).length === 0" class="empty-list">
          暂无记录
        </div>
      </div>
    </div>

    <!-- 使用说明 -->
    <div class="instructions">
      <h3>使用说明</h3>
      <div class="instruction-content">
        <div class="instruction-item">
          <div class="instruction-icon">📡</div>
          <div class="instruction-text">点击<strong>生成新呼号</strong>随机生成呼号</div>
        </div>
        <div class="instruction-item">
          <div class="instruction-icon">🔊</div>
          <div class="instruction-text">点击<strong>朗读解释法</strong>听完整发音</div>
        </div>
        <div class="instruction-item">
          <div class="instruction-icon">🔄</div>
          <div class="instruction-text">点击<strong>自动生成</strong>按设定间隔生成呼号</div>
        </div>
        <div class="instruction-item">
          <div class="instruction-icon">📝</div>
          <div class="instruction-text">点击单个字符或解释词可听单字发音</div>
        </div>
        <div class="instruction-item">
          <div class="instruction-icon">⏱️</div>
          <div class="instruction-text">点击最近呼号记录可快速切换</div>
        </div>
      </div>
    </div>

    <!-- 模态框 - 移动设备优化版 -->
    <transition name="modal-fade">
      <div v-if="modalVisible" class="modal-overlay" @click="closeModal">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3>{{ modalTitle }}</h3>
            <button class="modal-close" @click="closeModal" aria-label="关闭">
              <span aria-hidden="true">×</span>
            </button>
          </div>
          <div class="modal-body">
            {{ modalMessage }}
          </div>
          <div class="modal-footer">
            <button class="modal-btn" @click="closeModal">确定</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped>
.callsign-container {
  max-width: 800px; /* 增加到与其他组件一致的宽度 */
  margin: 0 auto;
  padding: 20px;
  font-family: Arial, sans-serif;
}

/* 移动端适配 - 增加内容区域宽度 */
@media (max-width: 768px) {
  .callsign-container {
    max-width: 100%; /* 使用100%宽度 */
    padding: 15px 10px; /* 减小左右内边距，提供更多内容空间 */
  }

  /* 调整其他元素以适应更宽的布局 */
  .callsign-display,
  .callsigns-history,
  .custom-callsign,
  .voice-settings,
  .auto-generate,
  .instructions {
    width: 100%;
    margin-left: 0;
    margin-right: 0;
  }
}

/* 中等屏幕适配 */
@media (min-width: 769px) and (max-width: 1024px) {
  .callsign-container {
    max-width: 800px; /* 保持与桌面版一致的宽度 */
  }
}

.callsign-display {
  background-color: #f5f5f5;
  border-radius: 10px;
  text-align: center;
  padding-bottom: 20px; /* 增加底部内边距 */
  overflow: hidden; /* 确保内容不会溢出容器 */
}

/* 确保标题在深色模式下可见 */
.callsign-display h2 {
  color: #333;
}

.callsign {
  display: flex;
  justify-content: center;
  margin: 20px 0;
}

.callsign-char {
  display: inline-block;
  width: 50px;
  height: 50px;
  line-height: 50px;
  margin: 0 5px;
  font-size: 28px;
  font-weight: bold;
  background-color: #4CAF50;
  color: white;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s;
}

.callsign-char:hover {
  transform: scale(1.1);
  background-color: #45a049;
}

.callsign-char.digit {
  background-color: #2196F3;
}

.phonetic {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
  padding: 0 15px 20px; /* 添加左右内边距和底部内边距 */
  overflow: hidden; /* 防止内容溢出 */
}

/* 调整每个项目的样式 */
.phonetic-item {
  display: flex;
  flex-direction: column; /* 垂直布局 */
  align-items: center;
  width: calc(25% - 10px); /* 一排四个，考虑间距 */
  padding: 10px;
  background-color: white;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s;
  box-sizing: border-box;
}

/* 确保最后两个项目居中 */
.phonetic-item:nth-last-child(1),
.phonetic-item:nth-last-child(2) {
  margin-left: auto;
  margin-right: auto;
}

.phonetic-item .char {
  font-weight: bold;
  font-size: 18px;
  margin-bottom: 5px;
}

.phonetic-item .word {
  font-size: 14px;
  color: #666;
  text-align: center;
}

/*
  注意：深色模式样式已移至全局样式文件 src/assets/dark-mode.css
*/

.actions {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 15px;
  margin: 20px 0;
}

.main-actions {
  display: flex;
  gap: 10px;
}

.generate-btn, .speak-btn, .auto-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s;
  min-width: 120px; /* 确保所有按钮最小宽度一致 */
  text-align: center; /* 确保文本居中 */
  height: 42px; /* 固定高度，确保所有按钮高度一致 */
  box-sizing: border-box; /* 确保padding不会增加总宽度/高度 */
}

.generate-btn {
  background-color: #4CAF50;
  color: white;
}

.generate-btn:hover {
  background-color: #45a049;
}

.speak-btn {
  background-color: #2196F3;
  color: white;
}

.speak-btn:hover {
  background-color: #0b7dda;
}

.auto-btn {
  background-color: #ff9800;
  color: white;
  font-weight: bold;
}

.auto-btn.active {
  background-color: #e65100;
}

.auto-btn.active:hover {
  background-color: #d32f2f;
}

/* 语音设置样式 */
.voice-settings {
  margin: 30px 0;
  padding: 20px;
  background-color: #f5f5f5;
  border-radius: 10px;
}

.setting-group {
  margin: 15px 0;
  display: flex;
  align-items: center;
}

.setting-group label {
  width: 60px;
  font-weight: bold;
}

.slider-container input[type="range"] {
  flex: 1;
  width: 100%;
  margin: 0;
  padding: 0;
}

.slider-container .setting-value {
  width: 30px;
  text-align: center;
  flex-shrink: 0;
}

/* 自定义滑块样式 */
.slider {
  -webkit-appearance: none;
  appearance: none;
  height: 8px;
  border-radius: 4px;
  background: #ddd;
  /* 添加默认的CSS变量值 */
  --range-progress: 50%;
  /* 默认的渐变背景 */
  background: linear-gradient(to right, #2196F3 0%, #2196F3 var(--range-progress), #ddd var(--range-progress), #ddd 100%);
  outline: none;
  width: 100%;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #2196F3;
  cursor: pointer;
  border: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  margin-top: -5px;
}

.slider::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #2196F3;
  cursor: pointer;
  border: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

/* 确保滑块轨道样式一致 */
.slider::-webkit-slider-runnable-track {
  width: 100%;
  height: 8px;
  cursor: pointer;
  border-radius: 4px;
  background: transparent;
  margin: 0;
  padding: 0;
}

.slider::-moz-range-track {
  width: 100%;
  height: 8px;
  cursor: pointer;
  border-radius: 4px;
  background: transparent;
  margin: 0;
  padding: 0;
}

/* 设置组样式 */
.setting-group {
  display: flex;
  align-items: center;
  margin: 8px 0;
  padding: 0;
}

.setting-group label {
  min-width: 40px;
  margin-right: 8px;
  font-weight: bold;
  font-size: 0.9rem;
}

.setting-group .slider {
  flex: 1;
  margin: 0 8px;
}

.setting-group .setting-value {
  min-width: 25px;
  text-align: right;
  margin-left: 8px;
  font-size: 0.9rem;
}

select {
  flex: 1;
  padding: 8px;
  border-radius: 5px;
  border: 1px solid #ddd;
}

button {
  padding: 12px 24px;
  border: none;
  border-radius: 5px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s;
}

.generate-btn {
  background-color: #4CAF50;
  color: white;
}

.speak-btn {
  background-color: #2196F3;
  color: white;
}

.custom-callsign {
  margin: 20px 0; /* 减小上下间距，原来是 30px 0 */
  padding: 15px; /* 减小内边距，原来是 20px */
  background-color: #f5f5f5;
  border-radius: 10px;
}

.custom-callsign h3 {
  margin-top: 0;
  margin-bottom: 10px; /* 减小标题下方间距 */
  font-size: 16px; /* 可选：减小标题字体大小 */
}

.input-group {
  display: flex;
  margin: 10px 0; /* 减小上下间距，原来是 15px 0 */
}

input {
  flex: 1;
  padding: 6px 10px; /* 减小输入框高度，原来是 10px */
  font-size: 14px; /* 减小字体大小，原来是 16px */
  border: 1px solid #ddd;
  border-radius: 5px 0 0 5px;
  text-transform: uppercase;
  height: 32px; /* 显式设置高度 */
  box-sizing: border-box;
}

.input-group button {
  border-radius: 0 5px 5px 0;
  background-color: #4CAF50;
  color: white;
  padding: 6px 15px; /* 减小按钮内边距，使其与输入框高度匹配 */
  font-size: 14px; /* 减小字体大小 */
  height: 32px; /* 显式设置高度，与输入框一致 */
}

.hint {
  font-size: 12px; /* 减小提示文字大小，原来是 14px */
  color: #666;
  margin: 5px 0;
}

/* 使用说明区域样式 */
.instructions {
  margin: 15px 0;
  padding: 15px;
  border-radius: 8px;
  background-color: #f8f9fa;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.instructions h3 {
  font-size: 1rem;
  margin: 0 0 12px 0;
  color: #333;
  display: flex;
  align-items: center;
}

.instructions h3::before {
  content: "💡";
  margin-right: 8px;
}

.instruction-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 10px;
}

.instruction-item {
  display: flex;
  align-items: center;
  padding: 8px;
  border-radius: 6px;
  background-color: white;
  transition: transform 0.2s, box-shadow 0.2s;
}

.instruction-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
}

.instruction-icon {
  font-size: 1.2rem;
  margin-right: 10px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.instruction-text {
  font-size: 0.9rem;
  color: #555;
  line-height: 1.4;
}

.instruction-text strong {
  color: #2196F3;
  font-weight: 600;
}

/* 响应式调整 */
@media (max-width: 600px) {
  .instruction-content {
    grid-template-columns: 1fr;
  }

  .instruction-item {
    padding: 6px;
  }

  .instruction-icon {
    font-size: 1rem;
  }

  .instruction-text {
    font-size: 0.85rem;
  }
}

/* 滑块样式 */
input[type="range"] {
  -webkit-appearance: none;
  appearance: none;
  height: 10px;
  border-radius: 3px;
  background: #ddd; /* 默认背景色 */
  outline: none;
  width: 100%;
  transition: background 0.1s ease;
}

input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #2196F3;
  cursor: pointer;
  border: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

input[type="range"]::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #2196F3;
  cursor: pointer;
  border: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

/* 移除 Firefox 的默认滑块轨道样式 */
input[type="range"]::-moz-range-track {
  background: transparent;
}

/*
  注意：深色模式滑块样式已移至全局样式文件 src/assets/dark-mode.css
*/

/* 确保滑块在各浏览器中一致 */
input[type="range"]::-ms-track {
  width: 100%;
  cursor: pointer;
  background: transparent;
  border-color: transparent;
  color: transparent;
}

input[type="range"]::-ms-fill-lower {
  background: #4CAF50;
  border-radius: 3px;
}

input[type="range"]::-ms-fill-upper {
  background: #ddd;
  border-radius: 3px;
}

input[type="range"]::-ms-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #4CAF50;
  cursor: pointer;
  border: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.auto-btn {
  background-color: #ff9800;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: bold;
  min-width: 120px;
}

.auto-btn:hover {
  background-color: #f57c00;
}

.auto-btn.active {
  background-color: #f44336;
}

.auto-btn.active:hover {
  background-color: #d32f2f;
}

.countdown {
  font-size: 0.9em;
  margin-left: 5px;
}

.interval-control {
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}

.interval-control label {
  color: #333; /* 明确设置标签颜色 */
  font-weight: bold; /* 加粗使其更明显 */
}

.interval-input {
  width: 50px;
  padding: 5px;
  border: 1px solid #ddd;
  border-radius: 3px;
  text-align: center;
  height: 32px; /* 固定高度 */
  box-sizing: border-box;
}

/* 最近呼号记录样式 */
.callsigns-history {
  margin: 20px 0;
  padding: 15px;
  background-color: #f5f5f5;
  border-radius: 10px;
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.history-header h3 {
  margin: 0;
}

.history-actions {
  display: flex;
  gap: 8px;
}

.toggle-btn, .clear-btn {
  padding: 5px 10px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.toggle-btn {
  background-color: #2196F3;
  color: white;
}

.toggle-btn:hover {
  background-color: #1976D2;
}

.clear-btn {
  background-color: #F44336;
  color: white;
}

.clear-btn:hover {
  background-color: #D32F2F;
}

.callsigns-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 150px;
  overflow-y: auto;
  border-radius: 5px;
}

.callsign-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background-color: white;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.2s;
}

.callsign-item:hover {
  background-color: #e3f2fd;
}

.callsign-text {
  font-weight: bold;
  letter-spacing: 1px;
}

.callsign-time {
  color: #666;
  font-size: 0.9em;
}

.empty-list {
  text-align: center;
  color: #666;
  padding: 15px;
}

/* 响应式调整 */
@media (max-width: 600px) {
  .history-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .history-actions {
    width: 100%;
    justify-content: space-between;
  }

  .actions {
    flex-direction: column;
    align-items: center;
  }

  .main-actions {
    width: 100%;
    justify-content: center;
  }

  .generate-btn, .speak-btn, .auto-btn {
    width: 100%;
    max-width: 150px;
  }
}

/* 添加模态框样式 - 移动设备优化版 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 15px;
  padding-top: env(safe-area-inset-top, 15px);
  padding-bottom: env(safe-area-inset-bottom, 15px);
  padding-left: env(safe-area-inset-left, 15px);
  padding-right: env(safe-area-inset-right, 15px);
  animation: fadeIn 0.2s ease-out;
}

/* 模态框过渡动画 */
.modal-fade-enter-active, .modal-fade-leave-active {
  transition: opacity 0.3s;
}

.modal-fade-enter-from, .modal-fade-leave-to {
  opacity: 0;
}

/* 模态框内容动画 */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-content {
  background-color: white;
  border-radius: 12px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  animation: slideUp 0.3s ease-out;
  margin-bottom: 20vh; /* 在屏幕下方显示，更容易触及 */
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #ddd;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.modal-close {
  background: none;
  border: none;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  border-radius: 50%;
  margin: -10px;
  transition: background-color 0.2s;
}

.modal-close:hover, .modal-close:active {
  background-color: rgba(0, 0, 0, 0.1);
}

.modal-body {
  padding: 20px;
  font-size: 16px;
  line-height: 1.6;
  color: #333;
  max-height: 60vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch; /* 提升iOS滚动体验 */
}

.modal-footer {
  padding: 12px 20px 16px;
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #ddd;
}

/*
  注意：深色模式模态框样式已移至全局样式文件 src/assets/dark-mode.css
*/

.modal-btn {
  min-width: 80px;
  padding: 10px 20px;
  background-color: #2196F3;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 500;
  transition: background-color 0.3s, transform 0.1s;
}

.modal-btn:hover {
  background-color: #0b7dda;
}

.modal-btn:active {
  transform: scale(0.98);
}

/* 移动设备适配 */
@media (max-width: 480px) {
  .modal-content {
    width: 100%;
    max-width: none;
    border-radius: 12px;
    margin-bottom: 10vh;
  }

  .modal-header {
    padding: 14px 16px;
  }

  .modal-body {
    padding: 16px;
    font-size: 15px;
  }

  .modal-footer {
    padding: 12px 16px;
  }

  .modal-btn {
    width: 100%;
    padding: 12px 16px;
  }
}

/* 朗读按钮样式 */
.speak-btn {
  background-color: #2196F3;
  color: white;
  transition: all 0.3s ease;
}

.speak-btn:hover {
  background-color: #0b7dda;
}

/* 朗读按钮状态 */
.speak-btn.speaking {
  background-color: #FF9800; /* 更改为橙色 */
  color: white;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
  100% {
    opacity: 1;
  }
}

/* 添加深色模式适配 */
@media (prefers-color-scheme: dark) {
  .callsign-container {
    color: var(--color-text);
  }

  .callsign-display {
    background-color: #333;
  }

  .callsign-display h2 {
    color: var(--color-heading);
  }

  .phonetic-item {
    background-color: #2c2c2c;
  }

  .phonetic-item:hover {
    background-color: #3c3c3c;
  }

  .phonetic-item .char {
    color: var(--color-text);
  }

  .phonetic-item .word {
    color: var(--color-text-soft);
  }

  /* 最近呼号记录 */
  .callsigns-history {
    background-color: #333;
  }

  .callsigns-history h3 {
    color: var(--color-heading);
  }

  .callsign-item {
    background-color: #2c2c2c;
    border-color: #444;
  }

  .callsign-item:hover {
    background-color: #3c3c3c;
  }

  .callsign-text {
    color: var(--color-text);
  }

  .callsign-time {
    color: var(--color-text-mute);
  }

  .empty-list {
    color: var(--color-text-mute);
  }

  /* 使用说明 */
  .instructions {
    background-color: #333;
  }

  .instructions h3 {
    color: var(--color-heading);
  }

  .instruction-item {
    background-color: #2c2c2c;
    border-color: #444;
  }

  .instruction-text {
    color: var(--color-text-soft);
  }

  .instruction-text strong {
    color: var(--color-secondary);
  }

  /* 语音设置 */
  .voice-settings {
    background-color: #333;
  }

  .voice-settings h3 {
    color: var(--color-heading);
  }

  .setting-group label {
    color: var(--color-text);
  }

  .setting-value {
    color: var(--color-text);
  }

  /* 自定义呼号 */
  .custom-callsign {
    background-color: #333;
  }

  .custom-callsign h3 {
    color: var(--color-heading);
  }

  .custom-callsign input {
    background-color: #2c2c2c;
    border-color: #444;
    color: var(--color-text);
  }

  .custom-callsign input:focus {
    border-color: var(--color-secondary);
  }

  .interval-control label {
    color: var(--color-text);
  }

  .interval-input {
    background-color: #2c2c2c;
    border-color: #444;
    color: var(--color-text);
  }

  /* 模态框 */
  .modal-content {
    background-color: #333;
  }

  .modal-header {
    background-color: #2c2c2c;
    border-color: #444;
  }

  .modal-header h3 {
    color: var(--color-heading);
  }

  .modal-body {
    color: var(--color-text);
  }

  .modal-footer {
    border-color: #444;
  }

  /* 滑块样式 */
  input[type="range"], .slider {
    background: #444;
    /* 深色模式下的渐变背景 */
    background: linear-gradient(to right, var(--color-secondary) 0%, var(--color-secondary) var(--range-progress), #444 var(--range-progress), #444 100%);
  }

  input[type="range"]::-webkit-slider-thumb, .slider::-webkit-slider-thumb {
    background: var(--color-secondary);
  }

  input[type="range"]::-moz-range-thumb, .slider::-moz-range-thumb {
    background: var(--color-secondary);
  }
}

/* 修复字母解释法容器溢出问题 */
.phonetic {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
  padding-bottom: 20px; /* 添加底部内边距，确保内容不会溢出 */
  overflow: hidden; /* 防止内容溢出 */
}

.callsign-display {
  background-color: #f5f5f5;
  border-radius: 10px;
  text-align: center;
  padding-bottom: 20px; /* 增加底部内边距 */
  overflow: hidden; /* 确保内容不会溢出容器 */
}

/* 确保在小屏幕上也能正确显示 */
@media (max-width: 600px) {
  .phonetic {
    padding-bottom: 15px;
  }

  .phonetic-item {
    min-width: 60px; /* 减小最小宽度，使更多项目能在一行显示 */
    padding: 8px; /* 减小内边距 */
  }

  .phonetic-item .char {
    font-size: 16px; /* 减小字体大小 */
  }

  .phonetic-item .word {
    font-size: 12px; /* 减小字体大小 */
  }
}

/* 超小屏幕优化 */
@media (max-width: 360px) {
  .phonetic-item {
    min-width: 50px;
    padding: 6px;
  }

  .phonetic-item .char {
    font-size: 14px;
  }

  .phonetic-item .word {
    font-size: 11px;
  }
}

/* 深色模式适配 */
@media (prefers-color-scheme: dark) {
  .callsign-display {
    background-color: #333;
  }
}

/* 调整自动生成按钮及秒数行的样式，使其与上面按钮行长度一致 */
.actions {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 15px;
  margin: 20px 0;
}

.main-actions {
  display: flex;
  gap: 10px;
}

.auto-btn {
  background-color: #ff9800;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: bold;
  min-width: 120px;
}

.interval-control {
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}

.interval-input {
  width: 50px;
  padding: 5px;
  border: 1px solid #ddd;
  border-radius: 3px;
  text-align: center;
}

/* 移动端适配 */
@media (max-width: 600px) {
  .actions {
    flex-direction: column;
    align-items: center;
  }

  .main-actions {
    width: 100%;
    justify-content: center;
  }

  .generate-btn, .speak-btn, .auto-btn {
    width: 100%;
    max-width: 150px;
  }

  /* 确保自动生成按钮与上面按钮行宽度一致 */
  .auto-btn {
    flex: 1;
    max-width: 150px;
  }

  /* 调整间隔控制区域宽度 */
  .interval-control {
    flex: 1;
    max-width: 150px;
    justify-content: flex-end;
  }

  .interval-control label {
    font-size: 14px;
  }

  .interval-input {
    width: 40px;
    padding: 4px;
    font-size: 14px;
  }
}

/* 超小屏幕适配 */
@media (max-width: 400px) {
  .main-actions {
    flex-direction: column;
    align-items: center;
  }

  .generate-btn, .speak-btn, .auto-btn {
    max-width: 200px; /* 在超小屏幕上增加按钮宽度 */
  }

  .interval-control {
    margin-top: 10px;
    max-width: 200px;
    width: 100%;
    justify-content: center;
  }
}
</style>























